import React, { useEffect, useMemo, useState } from 'react';
import {
  TourGallerySection,
  TourTitleSection,
  TourInformationsSection,
  IncludedSection,
  DisclamerSection,
  MapSection,
  ItinerarySection,
  TourCardsSection,
  TourInformationsSectionSkeleton,
  IninerarySectionSkeleton,
  MapSectionSkeleton,
  IncludedSectionSkeleton,
  DisclamerSectionSkeleton,
  TourCardsSectionSkeleton,
} from 'views/rebranded-module/tours-module/TourViewV2/components';
import StyledBox from 'views/rebranded-module/tours-module/TourViewV2/TourViewV2.styles';
import { Box, Container, Grid } from '@mui/material';
import useAdabtiveMultiSizeImage from 'views/common/hooks/useAdabtiveMultiSizeImage';
import {
  StickNav,
  ImgBox,
  ReviewsV2,
  Typography700,
  TourWidgetSkeletonV2,
  StickNavSkeleton,
  ReviewsSkeletonV2,
  Faq,
  FaqSkeleton,
} from 'views/rebranded-module/common/components';
import {
  RiCalendar2Line,
  RiMap2Line,
  RiCheckFill,
  // RiAddLine, //comment addon temp
  RiStarLine,
  RiQuestionLine,
} from 'react-icons/ri';
import { TourBookingWidgetWrapper } from 'views/rebranded-module/tours-module/common/components';
import { GA_EVENT_CATEGORY } from 'lib/utils/CONSTANTS';
import { setGoogleAnalytics } from 'redux/features/globalData-slice';
import { PAGES_GA_IDENTIFIERS } from 'views/common/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import axios, { API } from 'lib/config/axios';
import { useTranslation } from 'next-i18next';
import { SelectTourFixedBar } from 'views/rebranded-module/tours-module/TourViewV2/components/SelectTourFixedBar';
import ScrollTo from 'lib/utils/ScrollTo';
import Head from 'next/head';
import generateMetaData from 'lib/utils/generateMetaData';
import { LazyLoad } from 'views/common/components';
import { TourFootNote } from 'views/rebranded-module/common/components/UI/TourFootNote/';
import { TourFootNoteSkeleton } from 'views/rebranded-module/common/components/UI/TourFootNote/TourFootNoteSkeleton';

const TourViewV2 = (props) => {
  const { serverData: tourData, bookingDetailsDataInit, cmsData } = props;
  const { t } = useTranslation();
  const getSizedImage = useAdabtiveMultiSizeImage();
  const [bookingDetails, setBookingDetails] = useState(bookingDetailsDataInit);
  const dispatch = useDispatch();
  const heroBackground = cmsData?.TourDetailsPage?.heroBackground;
  const heroBackgroundSizes = heroBackground?.sizes;
  const heroBackgroundAlt = heroBackground?.alt;
  const googleAnalytics = useSelector(
    (state) => state.globalData.googleAnalytics,
  );
  const showMembership = useSelector(
    (state) => state.globalData.showMembership,
  );
  const handleErrorResponse = useErrorsHandler();
  const isOffroad = useMemo(
    () => tourData.tour.tour_type.identifier === 'guided_offroad',
    [tourData],
  );
  const getInitialIncludeBikeState = () => {
    if (tourData.tour.acceptable_reservations_type === 'any_reservations') {
      if (bookingDetails && bookingDetails.selected_package_id) {
        const idx = bookingDetails.packages.findIndex(
          (pack) => pack.id === bookingDetails.selected_package_id,
        );
        if (bookingDetails.packages[idx].motorcycles_count) {
          return true;
        }
        return false;
      }
      return true;
    }

    if (
      tourData.tour.acceptable_reservations_type
      === 'with_motorcycle_reservations_only'
    ) {
      return true;
    }
    return false;
  };
  const [includeBike, setIncludeBike] = useState(getInitialIncludeBikeState());

  const getBookingDetails = (tourId, params, firstReq, reservationType) => {
    dispatch(setLoading(true));
    let withMotorcycle = params.with_motorcycle;
    if (firstReq) {
      if (reservationType === 'without_motorcycle_reservations_only') {
        withMotorcycle = false;
      } else withMotorcycle = true;
    }

    axios
      .get(API.tours.bookingDetails.replace('{tour_id}', tourId), {
        params: {
          with_motorcycle: withMotorcycle,
          occurrence_id: params.occurrence_id && +params.occurrence_id,
          package_id: params.package_id && +params.package_id,
          packages_count: params.packages_count && +params.packages_count,
          pickup_date: params.pickup_date,
        },
      })
      .then((res) => {
        setBookingDetails(res.data);
        dispatch(setLoading(false));
      })
      .catch((error) => {
        dispatch(setLoading(false));
        if (error.response.status == 403) {
          if (bookingDetails) {
            setBookingDetails((prev) => ({
              ...prev,
              pickup_date: params.pickup_date,
              packages: [],
            }));

            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: t('fe_er_motorcycle_tours:no_available_packages'),
              }),
            );
          } else if (includeBike === undefined) setIncludeBike(true);
        } else {
          handleErrorResponse(error, false);
        }
      });
  };

  const handleActionButtonClick = () => {
    ScrollTo('widget-container');
  };

  useEffect(() => {
    dispatch(
      setGoogleAnalytics({
        ...googleAnalytics,
        newsLetterEventCategory: GA_EVENT_CATEGORY.tour,
      }),
    );
    window.gtag('event', 'landing_page_view', {
      page_identifier: PAGES_GA_IDENTIFIERS.TOUR,
    });
  }, []);

  useEffect(() => {
    if (bookingDetails && typeof includeBike === 'undefined') {
      setIncludeBike(getInitialIncludeBikeState());
    }
  }, [bookingDetails]);

  const createStickNavTabs = () => {
    const menuTabsArr = [];

    tourData.activities.length > 0
      && menuTabsArr.push({
        label: cmsData.TourDetailsPage.itenaryNavText,
        id: 'itinerary',
        icon: <RiCalendar2Line />,
      });
    if (!isOffroad) {
      menuTabsArr.push({
        label: cmsData.TourDetailsPage.mapNavText,
        id: 'map',
        icon: <RiMap2Line />,
      });
    }
    (tourData.inclusions.included_items.length > 0
      || tourData.experience_tout !== null)
      && menuTabsArr.push({
        label: cmsData.TourDetailsPage.includedNavText,
        id: 'included',
        icon: <RiCheckFill />,
      });
    // temp comment addons
    // menuTabsArr.push({
    // label: cmsData.TourDetailsPage.addOnsNavText,
    // id: 'addons',
    // icon: <RiAddLine />,
    // });
    tourData.users_reviews?.reviews.length > 0
      && menuTabsArr.push({
        label: cmsData.TourDetailsPage.reviewsNavText,
        id: 'reviews',
        icon: <RiStarLine />,
      });
    tourData.faq_items?.length > 0
      && menuTabsArr.push({
        label: cmsData.TourDetailsPage.faqsNavText,
        id: 'faqs',
        icon: <RiQuestionLine />,
      });
    return menuTabsArr;
  };

  useEffect(() => {
    if (bookingDetails && typeof includeBike === 'undefined') {
      setIncludeBike(getInitialIncludeBikeState());
    }
  }, [bookingDetails]);

  const widgetProps = {
    bookingDetails,
    tourId: tourData.tour.id,
    startingPrice: tourData.tour.starting_price,
    includeBike,
    onIncludeBikeChange: setIncludeBike,
    getBookingDetails,
    acceptableReservationsType: tourData.tour.acceptable_reservations_type,
    tourType: tourData.tour.tour_type,
    content: cmsData.TourBookingWidgetComponent,
  };

  return (
    <StyledBox>
      <Head>
        {tourData?.seo_values && generateMetaData(tourData.seo_values)}
      </Head>
      <Box className="TVHeroContainer">
        <Box className="TVBGContainer">
          {heroBackgroundSizes ? (
            <ImgBox
              image={{
                src: getSizedImage(heroBackgroundSizes),
                alt: heroBackgroundAlt || 'tour-hero',
              }}
              width="100%"
              height="100%"
            />
          ) : null}
        </Box>
        <Box className="TVContantContainer">
          <Container maxWidth="xl">
            <TourTitleSection cmsData={cmsData} tour={tourData.tour} />
            {tourData.tour.gallery.length > 0 && (
              <TourGallerySection
                cmsData={cmsData.GalleryComponent}
                mediaItems={tourData.tour.gallery}
              />
            )}
          </Container>
        </Box>
      </Box>
      <Box mt={4} pb={6}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xl={8.5} lg={7.5} md={12} sm={12}>
              <LazyLoad
                skeleton={<TourInformationsSectionSkeleton />}
                dataLoaded={!!tourData}>
                <TourInformationsSection
                  cmsData={cmsData}
                  tour={tourData.tour}
                  isOffroad={isOffroad}
                />
              </LazyLoad>

              <LazyLoad skeleton={<StickNavSkeleton />} dataLoaded={tourData}>
                <StickNav stickNavTabs={createStickNavTabs()} lazyLoading />
              </LazyLoad>

              {tourData.activities.length >= 1 && (
                <Box id="itinerary" mt={2}>
                  <LazyLoad
                    threshold={0.01}
                    skeleton={(
                      <IninerarySectionSkeleton
                        itemsLength={tourData.activities.length}
                      />
                    )}
                    dataLoaded={!!tourData}>
                    <ItinerarySection
                      activities={tourData.activities}
                      sameDayTour={tourData.tour.days_count === 1}
                      cmsData={cmsData}
                    />
                  </LazyLoad>
                </Box>
              )}

              {!isOffroad && (
                <Box id="map" mt={6}>
                  <LazyLoad
                    skeleton={<MapSectionSkeleton />}
                    dataLoaded={!!tourData}>
                    <MapSection content={cmsData} tour={tourData.tour} />
                  </LazyLoad>
                </Box>
              )}

              {(tourData.inclusions.included_items.length > 0
                || tourData.experience_tout !== null) && (
                <Box id="included" mt={12} mb={8}>
                  <LazyLoad
                    skeleton={(
                      <IncludedSectionSkeleton
                        itemsLength={tourData.inclusions.included_items.length}
                      />
                    )}
                    dataLoaded={!!tourData}>
                    <IncludedSection
                      inclusions={tourData.inclusions}
                      includeBike={includeBike}
                      content={cmsData.TourDetailsPage}
                    />
                  </LazyLoad>
                </Box>
              )}
              {tourData.users_reviews?.reviews.length > 0 && (
                <Box id="reviews" mt={4}>
                  <LazyLoad
                    skeleton={(
                      <ReviewsSkeletonV2
                        itemsLength={tourData.users_reviews?.reviews.length}
                      />
                    )}
                    dataLoaded={!!tourData}>
                    <Typography700 variant="h6" component="p" mb={3}>
                      {cmsData.TourDetailsPage.reviewsSectionTitle}
                    </Typography700>
                    <ReviewsV2
                      reviewsData={tourData.users_reviews.reviews.slice(0, 5)}
                    />
                  </LazyLoad>
                </Box>
              )}
              {tourData.faq_items?.length > 0 && (
                <Box id="faqs" mt={8}>
                  <LazyLoad skeleton={<FaqSkeleton />} dataLoaded={tourData}>
                    <Typography700 variant="h5" component="p" mb={3}>
                      {cmsData.TourDetailsPage.faqsSectionTitle}
                    </Typography700>
                    <Faq data={tourData.faq_items.slice(0, 5)} />
                  </LazyLoad>
                </Box>
              )}
            </Grid>
            <Grid item xl={3.5} lg={4.5} md={12} sm={12} id="widget-container">
              <Box className="TVV2RightSide">
                <LazyLoad
                  skeleton={<TourWidgetSkeletonV2 />}
                  dataLoaded={!!tourData}>
                  <Box mb={2} pt={1} className="TVV2WidgetContainer">
                    <TourBookingWidgetWrapper {...widgetProps} />
                  </Box>
                </LazyLoad>

                <LazyLoad
                  skeleton={<TourFootNoteSkeleton />}
                  dataLoaded={!!tourData}>
                  <Box mb={2}>
                    <TourFootNote
                      content={cmsData.TourBookingWidgetComponent}
                    />
                  </Box>
                </LazyLoad>
                {tourData.tour.start_location.participating_in_membership
                  && showMembership && (
                    <LazyLoad
                      skeleton={<DisclamerSectionSkeleton />}
                      dataLoaded={!!tourData}>
                      <DisclamerSection content={cmsData.TourDetailsPage} />
                    </LazyLoad>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Box className="TVRelatedContainer">
          {tourData.related_tours.length > 0 && (
            <LazyLoad
              skeleton={<TourCardsSectionSkeleton />}
              dataLoaded={!!tourData}>
              <TourCardsSection
                cmsData={cmsData}
                relatedTours={tourData.related_tours}
              />
            </LazyLoad>
          )}
        </Box>
      </Box>
      <Box>
        <SelectTourFixedBar
          content={cmsData.TourDetailsPage}
          handleActionButtonClick={handleActionButtonClick}
        />
      </Box>
    </StyledBox>
  );
};

export { TourViewV2 };
